import { api } from '@ui/app/api';
import type {
    CreateCohortDto,
    UpdateCohortDto,
    CohortResponseDto,
    CohortResponseListDto,
    CohortCategoryDto,
    CreateCohortCategoryDto,
    UpdateCohortCategoryDto,
    SubCohortResponseDto,
    SubCohortListResponseDto,
    CreateSubCohortDto,
    UpdateSubCohortDto,
    CohortMembershipResponseDto,
    CohortMembershipListResponseDto,
    CreateCohortMembershipDto,
    CohortCategoryResponseDto,
    CohortCategoryListResponseDto,
    Filters,
    CohortNamesResponseDto,
} from '@packages/types';

export const cohortApi = api.injectEndpoints({
    endpoints: builder => ({
        // Cohorts
        createCohort: builder.mutation<CohortResponseDto, CreateCohortDto>({
            query: dto => ({
                url: 'cohorts',
                method: 'POST',
                body: dto,
            }),
            invalidatesTags: ['Cohorts'],
        }),

        deleteCohort: builder.mutation<{ deleted: true }, string>({
            query: id => ({
                url: `cohorts/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Cohorts'],
        }),

        updateCohort: builder.mutation<CohortResponseDto, { cohort_id: string; data: UpdateCohortDto }>({
            query: ({ cohort_id, data }) => ({
                url: `cohorts/${cohort_id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_res, _err, arg) => [{ type: 'Cohorts', id: arg.cohort_id }],
        }),

        getAllCohorts: builder.query<CohortResponseListDto, Filters | void>({
            query: (args: Filters = {}) => {
                const {
                    skip = 0,
                    take = 20,
                    search = '',
                    orderBy = 'created_at',
                    orderDirection = 'desc',
                    name,
                    category_name,
                    from_chat,
                } = args as any;
                const params: any = { skip, take, search, orderBy, orderDirection };
                if (name) params.name = name;
                if (category_name) params.category_name = category_name;
                if (typeof from_chat === 'boolean') params.from_chat = from_chat;
                return {
                    url: 'cohorts',
                    method: 'GET',
                    params,
                };
            },
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                const args =
                    (queryArgs as Filters & {
                        name?: string | string[];
                        category_name?: string | string[];
                        from_chat?: boolean;
                    }) || {};
                const {
                    search = '',
                    orderBy = 'created_at',
                    orderDirection = 'desc',
                    name,
                    category_name,
                    from_chat,
                } = args;
                return `${endpointName}(${JSON.stringify({ search, orderBy, orderDirection, name, category_name, from_chat })})`;
            },
            merge: (currentCache, newItems) => {
                if (!currentCache?.cohorts || !newItems?.cohorts) {
                    return newItems;
                }

                const currentIds = new Set(currentCache.cohorts.map(c => c.cohort_id));
                const hasOverlap = newItems.cohorts.some(c => currentIds.has(c.cohort_id));

                // If there is overlap with existing items (e.g., a refetch of first page), replace cache
                if (hasOverlap) {
                    return newItems;
                }

                // Otherwise, append only new items (pagination)
                const newOnly = newItems.cohorts.filter(c => !currentIds.has(c.cohort_id));
                return {
                    ...newItems,
                    cohorts: [...currentCache.cohorts, ...newOnly],
                    total: newItems.total,
                };
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                const current =
                    (currentArg as Filters & {
                        name?: string | string[];
                        category_name?: string | string[];
                        from_chat?: boolean;
                    }) || {};
                const previous =
                    (previousArg as Filters & {
                        name?: string | string[];
                        category_name?: string | string[];
                        from_chat?: boolean;
                    }) || {};

                const currentSearch = current.search || '';
                const previousSearch = previous.search || '';
                if (currentSearch !== previousSearch) {
                    return true;
                }

                // Check filter changes
                const currentName = Array.isArray(current.name) ? current.name.sort().join(',') : current.name;
                const previousName = Array.isArray(previous.name) ? previous.name.sort().join(',') : previous.name;
                if (currentName !== previousName) {
                    return true;
                }

                const currentCategoryName = Array.isArray(current.category_name)
                    ? current.category_name.sort().join(',')
                    : current.category_name;
                const previousCategoryName = Array.isArray(previous.category_name)
                    ? previous.category_name.sort().join(',')
                    : previous.category_name;
                if (currentCategoryName !== previousCategoryName) {
                    return true;
                }

                if (current.from_chat !== previous.from_chat) {
                    return true;
                }

                const currentSkip = current.skip ?? 0;
                const previousSkip = previous.skip ?? 0;
                const currentTake = current.take ?? 20;
                const previousTake = previous.take ?? 20;

                return currentSkip !== previousSkip || currentTake !== previousTake;
            },
            providesTags: result =>
                result?.cohorts
                    ? [
                          ...result.cohorts.map(cohort => ({
                              type: 'Cohorts' as const,
                              id: cohort.cohort_id,
                          })),
                          { type: 'Cohorts' as const, id: 'LIST' },
                      ]
                    : [{ type: 'Cohorts', id: 'LIST' }],
            keepUnusedDataFor: 300,
        }),

        // Cohort Category
        getCohortCategory: builder.query<CohortCategoryResponseDto, string>({
            query: category_id => ({
                url: `cohort-categories/${category_id}`,
                method: 'GET',
            }),
        }),

        // Cohort Category
        getAllCohortCategories: builder.query<CohortCategoryListResponseDto, void>({
            query: () => ({
                url: 'cohort-categories',
                method: 'GET',
            }),
        }),

        createCohortCategory: builder.mutation<CohortCategoryDto, CreateCohortCategoryDto>({
            query: dto => ({
                url: 'cohort-categories',
                method: 'POST',
                body: dto,
            }),
        }),

        updateCohortCategory: builder.mutation<CohortCategoryDto, { id: string; dto: UpdateCohortCategoryDto }>({
            query: ({ id, dto }) => ({
                url: `cohort-categories/${id}`,
                method: 'PATCH',
                body: dto,
            }),
        }),

        deleteCohortCategory: builder.mutation<{ deleted: true }, string>({
            query: id => ({
                url: `cohort-categories/${id}`,
                method: 'DELETE',
            }),
        }),

        // Cohort Memberships
        getCohortMemberships: builder.query<CohortMembershipListResponseDto, string>({
            query: cohort_id => ({
                url: `cohort-memberships/${cohort_id}`,
                method: 'GET',
            }),
        }),

        createCohortMembership: builder.mutation<CohortMembershipResponseDto, CreateCohortMembershipDto>({
            query: dto => ({
                url: 'cohort-memberships',
                method: 'POST',
                body: dto,
            }),
        }),

        deleteCohortMembership: builder.mutation<{ deleted: true }, string>({
            query: membership_id => ({
                url: `cohort-memberships/${membership_id}`,
                method: 'DELETE',
            }),
        }),

        getCohortNames: builder.query<CohortNamesResponseDto, void>({
            query: () => ({
                url: 'cohorts/cohort-names',
                method: 'GET',
            }),
        }),

        getCohortsByPatientId: builder.query<
            CohortResponseListDto,
            {
                patientId: string;
                page?: number;
                pageSize?: number;
                search?: string;
                orderBy?: string;
                orderDirection?: 'asc' | 'desc';
            }
        >({
            query: ({ patientId, page, pageSize, search, orderBy, orderDirection }) => ({
                url: `cohorts/by-patient/${patientId}`,
                method: 'GET',
                params: {
                    ...(page !== undefined && { page }),
                    ...(pageSize !== undefined && { pageSize }),
                    ...(search && { search }),
                    ...(orderBy && { orderBy }),
                    ...(orderDirection && { orderDirection }),
                },
            }),
            providesTags: result =>
                result?.cohorts
                    ? [
                          ...result.cohorts.map(cohort => ({
                              type: 'Cohorts' as const,
                              id: cohort.cohort_id,
                          })),
                          { type: 'Cohorts' as const, id: 'LIST' },
                      ]
                    : [{ type: 'Cohorts', id: 'LIST' }],
        }),
    }),
});

export const {
    useCreateCohortMutation,
    useUpdateCohortMutation,
    useDeleteCohortMutation,
    useGetAllCohortsQuery,
    useLazyGetAllCohortsQuery,
    useGetAllCohortCategoriesQuery,
    useGetCohortCategoryQuery,
    useCreateCohortCategoryMutation,
    useUpdateCohortCategoryMutation,
    useDeleteCohortCategoryMutation,
    useGetCohortMembershipsQuery,
    useCreateCohortMembershipMutation,
    useDeleteCohortMembershipMutation,
    useGetCohortNamesQuery,
    useGetCohortsByPatientIdQuery,
} = cohortApi;
